import { Ban } from "lucide-react";

import type { ReferralStats as Stats } from "../api";
import { formatCount } from "./signingRollout";
import styles from "./ReferralStats.module.css";

interface Props {
  /** The answer, or undefined while it is in flight or after it failed. */
  stats: Stats | undefined;
  /** The request itself failed — the settings below are unaffected. */
  failed: boolean;
}

/**
 * The referral counters, above the section's fields rather than inside a
 * FieldRow: they are read-only, and the row's left column would promise a
 * config key they do not have.
 *
 * They stay in this card all the same, and that is the whole reason for the
 * strip: the trigger and the ladder are chosen by these three numbers, and sending
 * an operator to another page for them means getting the choice made blind.
 * Same role, and the same three states, as `RequestSigningStats` two sections
 * below.
 *
 * Mockup: .codelane/artifacts/t-162/config-invites.html L334-L344.
 */
export function ReferralStats({ stats, failed }: Props) {
  // Still in flight: nothing is claimed, in either direction.
  if (!failed && !stats) return null;

  // The counters did not answer. A breakage, and it says so — every setting
  // below is unaffected and still saves.
  if (failed || !stats) {
    return (
      <div className={`${styles.usage} ${styles.empty}`}>
        <span className={styles.emptyIcon}>
          <Ban size={16} />
        </span>
        <div>
          <div className={styles.emptyTitle}>No data</div>
          <div className={styles.emptyText}>
            The referral counters did not answer. The settings below still save
            — only the numbers are missing.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.usage}>
      <div className={styles.head}>
        <span className={styles.kicker}>Referrals</span>
        <span className={styles.since}>last {stats.window_days} days</span>
      </div>
      <div className={styles.figures}>
        <div>
          <div className={styles.key}>Accepted</div>
          <div className={styles.value}>{formatCount(stats.accepted)}</div>
        </div>
        <div>
          <div className={styles.key}>Rewarded</div>
          {/* Pairs, not players: one grant here is two inbox items, and the
              figure would otherwise read as half of what it counts. */}
          <div className={styles.value}>
            {formatCount(stats.rewarded)} <span className={styles.of}>pairs</span>
          </div>
        </div>
        <div>
          <div className={styles.key}>Waiting for the trigger</div>
          <div className={styles.value}>{formatCount(stats.waiting)}</div>
        </div>
      </div>
    </div>
  );
}
